import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AlertProps } from 'ui';

import { TiltDirection } from '../../types';

export enum MessagePriority {
  TEMPORARY = 1,
  DEVICE_TILT = 2,
  DEVICE_STABILITY = 3,
}

export type AlertMessage = {
  message: string;
  severity: AlertProps['severity'];
  priority: MessagePriority;
  duration?: number;
};

export type AlertMessageState = {
  showAlert: boolean;
  message?: string;
  title?: string;
  severity: AlertProps['severity'];
  tiltDirection?: TiltDirection;
};

export const useAlertMessagePriority = ({
  isDeviceReady,
  tiltDirection,
  skipGuidance = false,
}: {
  isDeviceReady: boolean;
  tiltDirection: TiltDirection | undefined;
  skipGuidance?: boolean;
}) => {
  const [temporaryMessage, setTemporaryMessage] = useState<AlertMessage | null>(null);
  const alertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flashMessage = useCallback(
    ({
      message,
      severity = 'info',
      duration = 1000,
    }: {
      message: string;
      severity?: AlertProps['severity'];
      duration?: number;
    }) => {
      const alertMessage: AlertMessage = {
        duration,
        message,
        priority: MessagePriority.TEMPORARY,
        severity,
      };

      setTemporaryMessage(alertMessage);

      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }

      alertTimerRef.current = setTimeout(() => {
        setTemporaryMessage(null);
        alertTimerRef.current = null;
      }, duration);
    },
    []
  );

  const currentMessage = useMemo(() => {
    if (temporaryMessage) {
      return temporaryMessage;
    }

    if (skipGuidance) {
      return null;
    }

    if (tiltDirection) {
      return {
        message: `Tilt device ${tiltDirection}`,
        priority: MessagePriority.DEVICE_TILT,
        severity: 'error' as const,
      };
    }

    if (!isDeviceReady) {
      return {
        message: 'Keep device level and still',
        priority: MessagePriority.DEVICE_STABILITY,
        severity: 'warning' as const,
      };
    }

    return null;
  }, [temporaryMessage, tiltDirection, isDeviceReady, skipGuidance]);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }
    };
  }, []);

  const alertState: AlertMessageState = {
    message: currentMessage?.message,
    severity: currentMessage?.severity || 'info',
    showAlert: currentMessage !== null,
    tiltDirection: currentMessage?.priority === MessagePriority.DEVICE_TILT ? tiltDirection : undefined,
  };

  return {
    alertState,
    flashMessage,
  };
};
